// Bileli · modules/orgs/model — organization REPOSITORY (cookie session via shared httpClient).
// fetchMyOrgs/pickDefaultOrg moved verbatim from modules/teach/model/teacher.api.ts (PR #184 Programs
// slice) — orgs is their canonical home; teach re-imports through the barrel. New here: the enriched
// public profile (GET /orgs/{slug}), the provider-page course list, and OWNER/ADMIN profile editing.

import type { z } from 'zod';

import type {
  BileliOrg, BileliOrgProfile, OrgCourse, OrgInvite, OrgInviteAccept, OrgInviteMint, OrgInvitePreview,
  OrgInviteRole, OrgMember, OrgProgram, OrgRole,
} from './orgs.types';

import { bileliApiJson, bileliApiVoid, parseApi, schemas } from '../../../shared';

// bileliApiJson only sets Accept; a JSON body needs Content-Type too, or the backend rejects it.
const JSON_HEADERS = { 'Content-Type': 'application/json' };

// Exhaustiveness-pinned: adding a member to OrgRole without updating this record is a COMPILE error,
// so a new backend role can never silently degrade at the decode sites below.
const ORG_ROLE_SET: Record<OrgRole, true> = { OWNER: true, ADMIN: true, TEACHER: true, INSTRUCTOR: true };
const ORG_ROLES = Object.keys(ORG_ROLE_SET) as OrgRole[];

// ── My orgs (GET /me/orgs) ────────────────────────────────────────────────────────────────────────────
export async function fetchMyOrgs(): Promise<BileliOrg[]> {
  const raw = await bileliApiJson<unknown>('/me/orgs');
  const orgs = parseApi(schemas.OrgsSchema, raw, 'GET /me/orgs');
  return orgs.map((o) => ({
    id: o.id,
    slug: o.slug,
    name: o.name,
    kind: o.kind,
    createdAt: o.createdAt,
    // Unknown-tolerant decode (mapper, not zod): future roles degrade to undefined = read-only.
    role: ORG_ROLES.includes(o.role as OrgRole) ? (o.role as OrgRole) : undefined,
    // Bileli 2026-07-29: SHOWROOM REMOVED (backend) — org showroom mapper lines commented out.
    // showroomStatus: o.showroomStatus ?? undefined,
    // showroomUsername: o.showroomUsername ?? undefined,
    // showroomPeerId: o.showroomPeerId ?? undefined,
  }));
}

// ── Rename an INSTITUTIONAL org (PATCH /orgs/{slug} — ADMIN-gated; #277) ─────────────────────────────
// Per-field PATCH semantics: send ONLY the name (the guide's rule — never resend the whole form).
// PERSONAL orgs 409 server-side (their name mirrors the profile display name); slug never changes.
export async function renameOrg(slug: string, name: string): Promise<void> {
  await bileliApiJson(`/orgs/${slug}`, {
    method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ name }),
  });
}

// ── Create a workspace (POST /orgs — any authed user; caller becomes OWNER) ──────────────────────────
// The V3 header's «＋ Новое пространство» inline flow (teach-header round). Server generates the slug.
export async function createOrg(name: string): Promise<BileliOrg> {
  const raw = await bileliApiJson<unknown>('/orgs', {
    method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name }),
  });
  const o = parseApi(schemas.OrgSchema, raw, 'POST /orgs');
  return {
    id: o.id,
    slug: o.slug,
    name: o.name,
    kind: o.kind,
    createdAt: o.createdAt,
    role: 'OWNER', // the creator is inserted as OWNER server-side; /me/orgs doesn't send role yet
  };
}

// Ported read-only from frontend/web modules/orgs/lib/pick-default-org.ts — personal first, else newest.
export function pickDefaultOrg(orgs: BileliOrg[]): BileliOrg | null {
  if (!orgs.length) return null;
  const personal = orgs.find((o) => o.kind === 'PERSONAL');
  if (personal) return personal;
  const sorted = [...orgs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  return sorted[0] ?? null;
}

// ── Public provider profile (GET /orgs/{slug}) ────────────────────────────────────────────────────────
// 404 = unknown slug OR a PERSONAL org viewed by a non-member — callers render a friendly error state.
export async function fetchOrgProfile(slug: string): Promise<BileliOrgProfile> {
  const raw = await bileliApiJson<unknown>(`/orgs/${encodeURIComponent(slug)}`);
  const dto = parseApi(schemas.OrganizationProfileSchema, raw, 'GET /orgs/{slug}');
  return {
    id: dto.id,
    slug: dto.slug,
    name: dto.name,
    kind: dto.kind === 'INSTITUTIONAL' ? 'INSTITUTIONAL' : dto.kind === 'PERSONAL' ? 'PERSONAL' : 'UNKNOWN',
    logoUrl: dto.logoUrl ?? undefined,
    bio: dto.bio ?? undefined,
    courseCount: dto.courseCount,
    studentCount: dto.studentCount,
    ratingAvg: dto.ratingAvg ?? undefined, // null = no reviews — stays undefined, views render nothing
    ratingCount: dto.ratingCount,
    createdAt: dto.createdAt,
  };
}

// ── Provider-page course list (GET /orgs/{slug}/courses) ─────────────────────────────────────────────
function minCohortPrice(cohorts?: Array<{ priceMinor?: number | null }> | null): number | undefined {
  const prices = (cohorts ?? []).map((c) => c.priceMinor).filter((p): p is number => typeof p === 'number');
  if (!prices.length) return undefined;
  return Math.min(...prices);
}

export async function fetchOrgCourses(slug: string): Promise<OrgCourse[]> {
  const raw = await bileliApiJson<unknown>(`/orgs/${encodeURIComponent(slug)}/courses`);
  const items = parseApi(schemas.OrgCoursesSchema, raw, 'GET /orgs/{slug}/courses');
  return items.map((dto) => ({
    id: dto.id,
    title: dto.title,
    coverUrl: dto.coverImageUrl ?? undefined,
    ratingAvg: dto.ratingAvg ?? undefined,
    ratingCount: dto.ratingCount ?? undefined,
    priceMinor: minCohortPrice(dto.cohorts),
  }));
}

// ── Provider-page program list (GET /orgs/{slug}/programs) ───────────────────────────────────────────
// Own tiny fetch here (not modules/programs' fetchOrgPrograms) so orgs stays a leaf — a programs → orgs
// edge already exists, so orgs → programs would cycle. The shared ProgramSummariesSchema does the parsing.
type ProgramSummaryDto = z.infer<typeof schemas.ProgramSummarySchema>;

/** PUBLISHED-only + field map. Exported for unit tests. The endpoint returns ALL states to org members,
 *  but the provider page is public, so a member viewing their own page must still see PUBLISHED only. */
export function toOrgPrograms(items: ProgramSummaryDto[]): OrgProgram[] {
  return items
    .filter((dto) => dto.state === 'PUBLISHED')
    .map((dto) => ({
      id: dto.id,
      slug: dto.slug,
      titleKy: dto.titleKy,
      titleRu: dto.titleRu,
      coverUrl: dto.coverUrl ?? undefined,
      courseCount: dto.courseCount,
      ratingAvg: dto.ratingAvg ?? undefined, // null = no reviews — stays undefined, the card renders nothing
      ratingCount: dto.ratingCount,
    }));
}

export async function fetchOrgPrograms(slug: string): Promise<OrgProgram[]> {
  const raw = await bileliApiJson<unknown>(`/orgs/${encodeURIComponent(slug)}/programs`);
  return toOrgPrograms(parseApi(schemas.ProgramSummariesSchema, raw, 'GET /orgs/{slug}/programs'));
}

// ── Org profile editing (OWNER/ADMIN only — TEACHER cannot edit the org itself) ──────────────────────
/** PATCH /orgs/{slug} — the `bio` key is REQUIRED by the backend (send null to clear; omitting fails). */
export async function updateOrgBio(slug: string, bio: string | null): Promise<void> {
  await bileliApiVoid(`/orgs/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ bio }),
  });
}

/** PUT /orgs/{slug}/logo — same two-step media bind as covers; null clears. Media must belong to the org. */
export async function setOrgLogo(slug: string, mediaId: string | null): Promise<void> {
  await bileliApiVoid(`/orgs/${encodeURIComponent(slug)}/logo`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ mediaId }),
  });
}

// Kind badge copy — RU source strings; t() resolves ky at usage («Академия» identical, «Мугалим» exists).
export const ORG_KIND_LABEL_RU: Record<'PERSONAL' | 'INSTITUTIONAL', string> = {
  PERSONAL: 'Преподаватель',
  INSTITUTIONAL: 'Академия',
};

// ── Org invite links + roster (backend PRs #220/#222; docs/backend org-invites guide) ────────────────
// Management endpoints are OWNER/ADMIN-gated server-side (403 for TEACHER); redemption needs only an
// authenticated session. Errors are RFC-7807 → BileliApiError carries .status (+ .detail).

function decodeInviteRole(role: string): OrgInviteRole {
  // Decode every real role EXACTLY — the consent screen must never mislabel an OWNER (money-bearing)
  // invite as "teaching", which the old ADMIN-or-else ternary did. Only unknown FUTURE roles degrade
  // to the TEACHER display default.
  return ORG_ROLES.includes(role as OrgRole) ? (role as OrgInviteRole) : 'TEACHER';
}

function toOrgInvite(dto: z.infer<typeof schemas.OrgInviteSchema>): OrgInvite {
  return {
    id: dto.id,
    orgId: dto.orgId,
    role: decodeInviteRole(dto.role),
    createdAt: dto.createdAt,
    expiresAt: dto.expiresAt,
  };
}

function toOrgMember(dto: z.infer<typeof schemas.OrgMemberSchema>): OrgMember {
  return {
    id: dto.id,
    orgId: dto.orgId,
    userId: dto.userId,
    role: ORG_ROLES.includes(dto.role as OrgRole) ? (dto.role as OrgRole) : 'TEACHER',
    createdAt: dto.createdAt,
    // Bileli: present only once the backend ships them — nullish → undefined, house convention.
    displayName: dto.displayName ?? undefined,
    telegramUsername: dto.telegramUsername ?? undefined,
    avatarUrl: dto.avatarUrl ?? undefined,
  };
}

function toOrg(dto: z.infer<typeof schemas.OrgSchema>): BileliOrg {
  return {
    id: dto.id,
    slug: dto.slug,
    name: dto.name,
    kind: dto.kind,
    createdAt: dto.createdAt,
    role: ORG_ROLES.includes(dto.role as OrgRole) ? (dto.role as OrgRole) : undefined,
  };
}

/** POST /orgs/{slug}/invites — mint a single-use link. The returned token is shown ONCE; never persist. */
export async function mintOrgInvite(slug: string, role: OrgInviteRole): Promise<OrgInviteMint> {
  const raw = await bileliApiJson<unknown>(`/orgs/${encodeURIComponent(slug)}/invites`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ role }),
  });
  const dto = parseApi(schemas.OrgInviteMintSchema, raw, 'POST /orgs/{slug}/invites');
  return { invite: toOrgInvite(dto.invite), token: dto.token };
}

/** GET /orgs/{slug}/invites — ACTIVE (redeemable) invites only; expired/used drop out automatically. */
export async function fetchOrgInvites(slug: string): Promise<OrgInvite[]> {
  const raw = await bileliApiJson<unknown>(`/orgs/${encodeURIComponent(slug)}/invites`);
  return parseApi(schemas.OrgInvitesSchema, raw, 'GET /orgs/{slug}/invites').map(toOrgInvite);
}

/** DELETE /orgs/{slug}/invites/{id} — revoke; idempotent 204. */
export async function revokeOrgInvite(slug: string, inviteId: string): Promise<void> {
  await bileliApiVoid(`/orgs/${encodeURIComponent(slug)}/invites/${encodeURIComponent(inviteId)}`, {
    method: 'DELETE',
  });
}

/** GET /orgs/{slug}/members — the roster (userIds only; display data is deliberately not included). */
export async function fetchOrgMembers(slug: string): Promise<OrgMember[]> {
  const raw = await bileliApiJson<unknown>(`/orgs/${encodeURIComponent(slug)}/members`);
  return parseApi(schemas.OrgMembersSchema, raw, 'GET /orgs/{slug}/members').map(toOrgMember);
}

/** DELETE /orgs/{slug}/members/{userId} — remove; 400 = last-OWNER guard (surface .detail). */
export async function removeOrgMember(slug: string, userId: string): Promise<void> {
  await bileliApiVoid(`/orgs/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

/** POST /org-invites/preview — the confirm screen's data; does NOT consume. 404 bad link · 410 dead. */
export async function previewOrgInvite(token: string): Promise<OrgInvitePreview> {
  const raw = await bileliApiJson<unknown>('/org-invites/preview', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ token }),
  });
  const dto = parseApi(schemas.OrgInvitePreviewSchema, raw, 'POST /org-invites/preview');
  return { org: toOrg(dto.org), role: decodeInviteRole(dto.role), expiresAt: dto.expiresAt };
}

/** POST /org-invites/accept — joins + consumes (unless alreadyMember). Races resolve server-side. */
export async function acceptOrgInvite(token: string): Promise<OrgInviteAccept> {
  const raw = await bileliApiJson<unknown>('/org-invites/accept', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ token }),
  });
  const dto = parseApi(schemas.OrgInviteAcceptSchema, raw, 'POST /org-invites/accept');
  return { org: toOrg(dto.org), member: toOrgMember(dto.member), alreadyMember: dto.alreadyMember };
}
